import React, { useState, useEffect } from 'react';
import Card from '../../../../shared/components/Card';
import usePageTranslation from '../../../../shared/hooks/usePageTranslation';

export interface CRMRow {
  id: string;
  name: string;
  owner: string;
  address: string;
  timeInfo: string;
  isPrivate?: boolean;
}

export interface CRMDetail {
  applications: { name: string; count: number }[];
  vehiclesSummary: string;
  assignedVehicles: { id: string; nom: string; statut: string; construction: string }[];
}

const MOCK_CRMS: CRMRow[] = [
  { id: '1', name: 'Ile de la Cité', owner: 'firedept60', address: 'Place Louis Lépine, 75004 Paris, Paris 4e Arrondissement', timeInfo: '10:52:16' },
  { id: '2', name: 'CRM', owner: 'betagames', address: '48 Rue Thiac, 33000 Bordeaux, Saint-Seurin', timeInfo: '2 jours 00:08:10' },
  { id: '3', name: 'CRM 4', owner: 'Clément.694', address: 'Chemin du Frogier Inférieur, 06690 Tourrette-Levens', timeInfo: '1 jour 09:26:58' },
  { id: '4', name: 'MARSEILLE', owner: 'matheo2503', address: 'Boulevard Périphérique Intérieur, 75013 Paris 13e Arrondissement', timeInfo: '6 jours 06:18:13' },
  { id: '5', name: 'BORDEAUX', owner: 'Jonathan68', address: 'Rue de la République, 33000 Bordeaux', timeInfo: '10:53:16' },
  { id: '6', name: 'Crm brive', owner: 'POMPLARD06', address: 'Place de la Brive, 19100 Brive-la-Gaillarde', timeInfo: '3 jours 12:00:00' },
  { id: '7', name: 'STRASBOURG', owner: 'Babyshark', address: 'Place Kléber, 67000 Strasbourg', timeInfo: '5 jours 08:30:00' },
  { id: '8', name: 'CRM 75', owner: 'YohannL', address: 'Avenue des Champs-Élysées, 75008 Paris', timeInfo: '4 jours 14:22:00' },
];

const MOCK_CRM_DETAILS: Record<string, CRMDetail> = {
  '1': {
    applications: [{ name: 'Hugo.coop', count: 1 }, { name: 'GlobalOperator21', count: 3 }, { name: 'firedept60', count: 91 }],
    vehiclesSummary: 'ReBP (12) - VR (61) - ELE (6) - VAT (3) - VTP (3) - RAM (3) - VCT (3) - VTU (2) - EPA (1) - FPT (1)',
    assignedVehicles: [
      { id: 'v1', nom: 'VTU Rouvray', statut: '4', construction: 'CIS Rouvray' },
      { id: 'v2', nom: 'VTU Aignay', statut: '2 En zone intermédiaire', construction: 'CIS Aignay-le-Duc' },
      { id: 'v3', nom: 'FPT 2 Montbard', statut: '4', construction: 'CIS Montbard' },
      { id: 'v4', nom: 'VTP Dijon', statut: '2 En zone intermédiaire', construction: 'CIS Dijon' },
      { id: 'v5', nom: 'VR Paris 13', statut: '4', construction: 'CIS Paris 13' },
    ],
  },
};

const DEFAULT_CRM_DETAIL: CRMDetail = {
  applications: [],
  vehiclesSummary: '-',
  assignedVehicles: [],
};

const AllianceCRMs: React.FC = () => {
  const { t } = usePageTranslation('game', 'AlliancePage');
  const [search, setSearch] = useState('');
  const [detailCRM, setDetailCRM] = useState<CRMRow | null>(null);
  const [selectAllVehicles, setSelectAllVehicles] = useState(false);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<Set<string>>(new Set());

  const filtered = MOCK_CRMS.filter(
    (row) =>
      row.name.toLowerCase().includes(search.toLowerCase()) ||
      row.owner.toLowerCase().includes(search.toLowerCase()) ||
      row.address.toLowerCase().includes(search.toLowerCase())
  );

  const detail = detailCRM ? (MOCK_CRM_DETAILS[detailCRM.id] ?? DEFAULT_CRM_DETAIL) : null;

  useEffect(() => {
    if (!detailCRM) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDetailCRM(null);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [detailCRM]);

  useEffect(() => {
    if (!detailCRM) {
      setSelectAllVehicles(false);
      setSelectedVehicleIds(new Set());
      return;
    }
  }, [detailCRM]);

  useEffect(() => {
    if (detail) {
      if (selectAllVehicles) {
        setSelectedVehicleIds(new Set(detail.assignedVehicles.map((v) => v.id)));
      } else {
        setSelectedVehicleIds(new Set());
      }
    }
  }, [selectAllVehicles, detail]);

  const toggleVehicle = (id: string) => {
    setSelectedVehicleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Card className="p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-bold text-white">{t('crms.title')}</h2>
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('crms.search')}
            className="w-full pl-4 pr-10 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white placeholder-gray-500 text-sm focus:ring-2 focus:ring-indigo-500"
          />
          <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-700">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-800/80 border-b border-gray-700">
              <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('crms.colName')}</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('crms.colOwner')}</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('crms.colAddress')}</th>
              <th className="px-4 py-3 text-sm font-semibold text-gray-300">{t('crms.colTime')}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-gray-500">
                  {t('crms.noResults')}
                </td>
              </tr>
            ) : (
              filtered.map((row) => (
                <tr key={row.id} className="border-b border-gray-700/50 hover:bg-gray-800/30">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setDetailCRM(row)}
                      className="inline-flex items-center px-3 py-1.5 rounded-lg font-medium text-indigo-400 bg-indigo-500/20 border border-indigo-500/40 hover:bg-indigo-500/30 hover:border-indigo-400/60 hover:text-indigo-300 transition-colors text-left"
                    >
                      {row.name}
                    </button>
                    {row.isPrivate && (
                      <span className="ml-2 text-red-400 text-xs" title={t('crms.private')}>🔒</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400">{row.owner}</td>
                  <td className="px-4 py-3 text-gray-400 text-sm max-w-xs truncate" title={row.address}>
                    {row.address}
                  </td>
                  <td className="px-4 py-3 text-gray-400 tabular-nums text-sm">{row.timeInfo}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal détail CRM (clic sur le nom) */}
      {detailCRM && detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDetailCRM(null)} aria-hidden />
          <div className="relative z-50 bg-gray-900 rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col border border-gray-700 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-red-800/30 shrink-0">
              <h3 className="text-xl font-bold text-white">{detailCRM.name}</h3>
              <button type="button" onClick={() => setDetailCRM(null)} className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-gray-700" aria-label={t('wall.close')}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">{t('crms.colOwner')}</span>
                  <span className="text-white flex items-center gap-1">
                    <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" /></svg>
                    {detailCRM.owner}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">{t('crms.colTime')} </span>
                  <span className="text-gray-300">{detailCRM.timeInfo}</span>
                </div>
              </div>
              {detail.applications.length > 0 && (
                <div>
                  <span className="text-gray-500 text-sm">{t('crms.application')} </span>
                  <span className="text-gray-300 text-sm">{detail.applications.map((a) => `${a.name} (${a.count})`).join(' - ')}</span>
                </div>
              )}
              <div>
                <span className="text-gray-500 text-sm">{t('crms.vehicles')} </span>
                <span className="text-gray-300 text-sm">{detail.vehiclesSummary}</span>
              </div>
              <div className="bg-blue-900/20 border border-blue-700/40 rounded-lg p-4 text-sm text-blue-200">
                {t('crms.infoBanner')}
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-300 mb-2">{t('crms.assignedVehicles')}</h4>
                <div className="overflow-x-auto rounded border border-gray-700">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="bg-gray-800/80 border-b border-gray-700">
                        <th className="px-3 py-2 w-10">
                          <label className="flex items-center gap-1 cursor-pointer text-gray-400">
                            <input type="checkbox" checked={selectAllVehicles} onChange={(e) => setSelectAllVehicles(e.target.checked)} className="rounded border-gray-600" />
                            <span>{t('crms.selectAll')}</span>
                          </label>
                        </th>
                        <th className="px-3 py-2 text-gray-300">{t('crms.vehicleName')}</th>
                        <th className="px-3 py-2 text-gray-300">{t('crms.vehicleStatus')}</th>
                        <th className="px-3 py-2 text-gray-300">{t('crms.vehicleConstruction')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.assignedVehicles.map((v) => (
                        <tr key={v.id} className="border-b border-gray-700/50">
                          <td className="px-3 py-2">
                            <input type="checkbox" checked={selectedVehicleIds.has(v.id)} onChange={() => toggleVehicle(v.id)} className="rounded border-gray-600" />
                          </td>
                          <td className="px-3 py-2"><span className="text-indigo-400">{v.nom}</span></td>
                          <td className="px-3 py-2"><span className="px-2 py-0.5 rounded bg-orange-500/20 text-orange-400 text-xs">{v.statut}</span></td>
                          <td className="px-3 py-2"><span className="text-indigo-400">{v.construction}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-700 bg-gray-800/50 shrink-0 flex justify-end">
              <button type="button" className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium">
                {t('crms.deploy')}
              </button>
            </div>
          </div>
        </div>
      )}

    </Card>
  );
};

export default AllianceCRMs;
